/**
 * ErrorBoundary - React Error Boundary for Admin Portal
 * 
 * Catches JavaScript errors in child components and displays
 * a fallback UI instead of crashing the entire application.
 */

import React, { Component } from 'react';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorId: `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Error info:', errorInfo);

    this.setState({
      error,
      errorInfo
    });

    this.logError(error, errorInfo);
  }

  logError = (error, errorInfo) => {
    const errorData = {
      message: error?.message,
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      errorId: this.state.errorId
    };

    if (process.env.NODE_ENV !== 'production') {
      console.group('[ErrorBoundary] Error Details');
      console.log('Error ID:', errorData.errorId);
      console.log('Message:', errorData.message);
      console.log('URL:', errorData.url);
      console.log('Timestamp:', errorData.timestamp);
      console.groupEnd();
    }
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoBack = () => {
    window.history.back();
  };

  render() {
    const { hasError, error, errorInfo, errorId } = this.state;
    const { children, fallback, showDetails = false, onError } = this.props;

    if (hasError) {
      if (onError) {
        onError(error, errorInfo);
      }

      if (fallback) {
        return typeof fallback === 'function'
          ? fallback({ error, errorInfo, retry: this.handleRetry })
          : fallback;
      }

      return (
        <div className="admin-error-boundary">
          <div className="admin-error-boundary__content">
            <div className="admin-error-boundary__icon">⚠️</div>
            <h2 className="admin-error-boundary__title">Something went wrong</h2>
            <p className="admin-error-boundary__message">
              We encountered an unexpected error. Please try again or contact support if the problem persists.
            </p>
            
            {errorId && (
              <p className="admin-error-boundary__id">
                Error ID: <code>{errorId}</code>
              </p>
            )}

            <div className="admin-error-boundary__actions">
              <button 
                className="admin-btn admin-btn--primary"
                onClick={this.handleRetry}
              >
                Try Again
              </button>
              <button 
                className="admin-btn admin-btn--secondary"
                onClick={this.handleReload}
              >
                Reload Page
              </button>
              <button 
                className="admin-btn admin-btn--secondary"
                onClick={this.handleGoBack}
              >
                Go Back
              </button>
            </div>

            {showDetails && error && (
              <details className="admin-error-boundary__details">
                <summary>Technical Details</summary>
                <pre className="admin-error-boundary__stack">
                  {error.toString()}
                  {errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return children;
  }
}

export const withErrorBoundary = (WrappedComponent, errorBoundaryProps = {}) => {
  const WithErrorBoundary = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundary.displayName = `WithErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithErrorBoundary;
};

export const SimpleErrorFallback = ({ error, retry }) => (
  <div className="admin-error-fallback admin-error-fallback--simple">
    <p className="admin-error-fallback__message">
      Failed to load this section
    </p>
    {retry && (
      <button onClick={retry} className="admin-btn admin-btn--secondary">
        Retry
      </button>
    )}
  </div>
);

export const CardErrorFallback = ({ title = 'Error', message = 'Failed to load data', retry }) => (
  <div className="admin-card admin-error-fallback admin-error-fallback--card">
    <div className="admin-error-fallback__content">
      <div className="admin-error-fallback__icon">⚠️</div>
      <h3 className="admin-error-fallback__title">{title}</h3>
      <p className="admin-error-fallback__message">{message}</p>
      {retry && (
        <button onClick={retry} className="admin-btn admin-btn--secondary">
          Try Again
        </button>
      )}
    </div>
  </div>
);

export default ErrorBoundary;
